import { ErrorBoundary } from '@sentry/react';
import { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import App, { AppBridge } from './App';
import { ReactComponent as ErrorSvg } from './Assets/error.svg';
import { ReactComponent as JFSErrorIconNew } from './Assets/JFS/errorIcon-new.svg';
import { ReactComponent as JFSErrorIcon } from './Assets/JFS/ErrorIcon.svg';
import { ReactComponent as YBServerErrorIcon } from './Assets/yesbank/error-triangle.svg';
import SIBErrorScreen from './Components/SIB/Common/ErrorScreen';
import { ReactComponent as Logo } from './Assets/yesbank/YesBank_WebView.svg';

import { DotLottiePlayer } from '@dotlottie/react-player';
import NativeLoader from './Components/NativeLoader';
import './index.css';
import { getJfsLoaderPath } from './Utils/assetUtils';
import { pushEventToParent } from './Utils/eventUtils';
import { captureException } from './Utils/sentryUtils';
import {
  getAltJourney,
  getJfsTheme,
  setAltJourney,
  setAltJourneyTheme,
  setJfsTheme,
} from './Utils/storageUtils';
import { altJourneyTheme, altJourneyType, JfsTheme } from './Utils/types';

export const renderErrorScreen = ({ error }: any) => {
    captureException(error, { isErrorBoundary: true });
 

  const isJFS = process.env.REACT_APP_TENANT_ID_JFS !== undefined;
  const isYesBank = process.env.REACT_APP_TENANT_ID_YB !== undefined;
  const isSIB = process.env.REACT_APP_TENANT_ID_SIB !== undefined;

  if (isJFS) {
    return (
      <div className='jfsErrorContainer'>
        <div className='jfsContentWrapper'>
          <div className='jfsErrorIcon'>
            {getJfsTheme() === JfsTheme.Purple ? (
              <JFSErrorIconNew />
            ) : (
              <JFSErrorIcon />
            )}
          </div>

          <h1 className='jfsErrorTitle'>Something went wrong.</h1>

          <p className='jfsErrorDescription'>We ran into a technical issue.</p>

          {process.env.REACT_APP_IS_MOCK === 'true' && error && (
            <div className='jfsErrorDetails'>{error.toString()}</div>
          )}
        </div>

        <div className='jfsButtonContainer'>
          <button
            className='jfsGoBackButton'
            onClick={() => {
              if (AppBridge && AppBridge.closeWebview) {
                AppBridge.closeWebview();
              }
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  } else if (isSIB) {
    return (
      <SIBErrorScreen
        fullScreen
        message='We encountered an unexpected error. Please step back and try again.'
        buttonText='Okay'
        onButtonClick={() => {
          pushEventToParent({ eventCode: 'SOMETHING_WENT_WRONG' });
        }}
      />
    );
  } else if (isYesBank) {
    return (
      <div className='ybErrorContainer'>
        <div className='ybContentWrapper'>
          <YBServerErrorIcon className='ybErrorIcon' />

          <h1 className='ybErrorTitle'>Server Error</h1>

          <p className='ybErrorDescription'>
            We are experiencing technical difficulties, please try again!
          </p>

          {process.env.REACT_APP_IS_MOCK === 'true' && error && (
            <div className='ybErrorDetails'>{error.toString()}</div>
          )}
        </div>

        <button
          className='ybGoBackButton'
          onClick={() => {
            pushEventToParent({ eventCode: 'GO_BACK' });
          }}
        >
          Go Back
        </button>
      </div>
    );
  } else {
    return (
      <div className='errorBoundaryRoot'>
        <ErrorSvg />
        <span className='errorBoundaryTitle'>Sorry!</span>
        <span className='errorBoundaryDescription'>
          Unfortunately, we have run into a technical issue
        </span>
        {process.env.REACT_APP_IS_MOCK === 'true' ? (
          <span>{error?.toString() ? error?.toString() : ''}</span>
        ) : (
          <></>
        )}
        <button
          className='errorButton'
          onClick={() => {
            pushEventToParent({ eventCode: 'GO_BACK' });
          }}
        >
          Go Back
        </button>
      </div>
    );
  }
};

const RootComponent = () => {
  const [isYesOnlineJourney, setIsYesOnlineJourney] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const meta = urlParams.get('meta');

    if (meta) {
      setAltJourney(altJourneyType.YESONLINE);
      setAltJourneyTheme(altJourneyTheme.YesGlory);
    }

    const themeColorName = urlParams.get('themecolorname');
    const storedTheme = sessionStorage.getItem('jfsTheme');
    const validThemes = Object.values(JfsTheme) as string[];
    const normalizedTheme = themeColorName?.toLowerCase();

    if (normalizedTheme && validThemes.includes(normalizedTheme)) {
      setJfsTheme(normalizedTheme);
    } else if (themeColorName) {
      captureException(
        new Error(`Invalid themecolorname query param: "${themeColorName}"`)
      );
      setJfsTheme(JfsTheme.Gold);
    } else if (!storedTheme) {
      setJfsTheme(JfsTheme.Gold);
    }

    setIsYesOnlineJourney(getAltJourney() === altJourneyType.YESONLINE);
  }, []);


  return (
    <BrowserRouter basename={process.env.REACT_APP_BASE_PATH}>
      {isYesOnlineJourney && (
        <div className='yesonline-logo-container'>
          <Logo className='yesonline-logo' />
        </div>
      )}
      <Routes>
        <Route
          path={'*'}
          element={
            <Suspense
              fallback={
                process.env.REACT_APP_TENANT_ID_JFS !== undefined ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                      background: '#fff',
                    }}
                  >
                    <DotLottiePlayer
                      src={getJfsLoaderPath()}
                      autoplay
                      loop
                      style={{
                        height: 48,
                        width: 48,
                      }}
                    />
                  </div>
                ) : (
                  <NativeLoader />
                )
              }
            >
              <ErrorBoundary fallback={renderErrorScreen}>
                <App />
              </ErrorBoundary>
            </Suspense>
          }
        ></Route>
      </Routes>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(<RootComponent />);
